import { FC } from 'react'

import * as RadixSwitch from '@radix-ui/react-switch'
import { styled } from 'styled-components'

import { onSurface, onSurfaceVariant, outline, onSurfaceDisabled } from 'core/styles'

export type SwitchProps = {
  checked: boolean
  onChange: (checked: boolean) => void
  disabled?: boolean
  id?: string
  'aria-label'?: string
  label?: string
  labelPosition?: 'left' | 'right'
  /** Spans the available width and pushes the label and the control to opposite ends. */
  fullWidth?: boolean
}

const Switch: FC<SwitchProps> = ({
  checked,
  onChange,
  disabled = false,
  id,
  'aria-label': ariaLabel,
  label,
  labelPosition = 'right',
  fullWidth = false,
}) => {
  // The label renders as a sibling of the Radix root, so it is not the control's
  // accessible name until it is passed in as one.
  const switchElement = (
    <StyledSwitchRoot
      $disabled={disabled}
      aria-label={ariaLabel ?? label}
      checked={checked}
      disabled={disabled}
      id={id}
      onCheckedChange={onChange}
    >
      <StyledSwitchThumb />
    </StyledSwitchRoot>
  )

  const labelElement = (
    <SwitchLabel $checked={checked} $disabled={disabled}>
      {label ?? (checked ? 'True' : 'False')}
    </SwitchLabel>
  )

  return (
    <SwitchContainer $fullWidth={fullWidth} $labelAlign={labelPosition}>
      {switchElement}
      {labelElement}
    </SwitchContainer>
  )
}

export default Switch

const SwitchContainer = styled.div<{ $labelAlign?: 'left' | 'right'; $fullWidth: boolean }>`
  display: ${({ $fullWidth }) => ($fullWidth ? 'flex' : 'inline-flex')};
  flex-direction: ${({ $labelAlign }) => ($labelAlign === 'left' ? 'row-reverse' : 'row')};
  gap: 8px;
  align-items: center;
  justify-content: ${({ $fullWidth }) => ($fullWidth ? 'space-between' : 'flex-start')};
  width: ${({ $fullWidth }) => ($fullWidth ? '100%' : 'auto')};
`

const SwitchLabel = styled.span<{ $checked: boolean; $disabled: boolean }>`
  min-width: 40px;
  min-height: 20px;
  text-align: center;
  color: ${({ $disabled }) => ($disabled ? onSurfaceVariant : 'inherit')};
  font-size: 14px;
  font-weight: 600;
`

const StyledSwitchRoot = styled(RadixSwitch.Root)<{ $disabled?: boolean }>`
  all: unset;
  position: relative;
  flex-shrink: 0;
  opacity: ${({ $disabled }) => ($disabled ? 0.6 : 1)};
  border-radius: 16px;
  box-shadow: 0 2px 10px ${outline};
  background-color: ${onSurfaceDisabled};
  cursor: ${({ $disabled }) => ($disabled ? 'not-allowed' : 'pointer')};
  width: 44px;
  height: 20px;
  -webkit-tap-highlight-color: rgb(0 0 0 / 0%);

  &[data-state='checked'] {
    background-color: ${onSurface};
  }
`

const StyledSwitchThumb = styled(RadixSwitch.Thumb)`
  display: block;
  transform: translateX(2px);
  transition: transform 100ms;
  border-radius: 32px;
  background-color: white;
  width: 20px;
  height: 16px;
  will-change: transform;

  &[data-state='checked'] {
    transform: translateX(22px);
  }
`
